// User-facing release notes, newest month first.
// Compiled from git history and !DEV_TRACKING.md. Only list features that are live
// in production; test-only or disabled features are added when they are released.
// Each entry: { month: 'YYYY-MM', title, summary?, features[], improvements[], fixes[] }

export const RELEASE_NOTES = [
  {
    month: '2026-10',
    title: 'October 2026',
    summary: 'AI Recommendations, rep-level email campaigns, cleaner reports and safer user administration.',
    features: [
      'AI Recommendations: the CRM suggests next steps for your customers (follow-ups, re-orders, at-risk accounts) and shows a short AI summary on the customer page.',
      'New Recommendations page with a compact "By customer" view. Each customer is one row with priority counts, and you expand only the customers you need. A "List" view is also available.',
      'Sales reps can run their own email campaigns. Rep-level access shows only your own customers, contacts, segments, templates and campaign analytics. Admins keep full access.',
      'Release Notes (this page) in the Help Center, so you can see what changed each month.',
    ],
    improvements: [
      'Recommendations panels on customer pages show the 10 most important items first, with a "Show all" option.',
      'Customer Support users can view team AI summaries and recommendations.',
      'Report filters for Opportunities and Customers list each sales rep once and match records reliably.',
      'The Customer Inactivity Report follows customers to the rep who currently owns them, so reps who take over a territory see the customers they inherited.',
      'AI summaries are checked more strictly, and the CRM explains clearly when a summary could not be shown.',
    ],
    fixes: [
      'Deactivating a user no longer hides their name on historical opportunity, task, customer and order assignments.',
      'New opportunity assignments are limited to active users titled Sales Rep or Sales Manager, including bulk assignment and Smart Import. Existing historical assignees are preserved.',
      'Creating a new user no longer signs the admin in as that user. The new user appears on the Users page immediately, with an audit log entry.',
      'The Full Name column on the Users page shows the name entered when the user was created.',
      'Calendar logs load correctly in Log Management.',
      'Fixed an issue that could give two tasks the same task number.',
      'Recommendations check whether a customer has a primary contact before suggesting outreach.',
      '"View As" shows exactly the pages and data the viewed user can see.',
      'Fixed email event tracking and sync logging.',
    ],
  },
  {
    month: '2026-09',
    title: 'September 2026',
    summary: 'Email campaign safeguards, report additions and more reliable user sync.',
    features: [
      'Email campaign editor: drag-and-drop personalization variables, and a check that every variable is valid before a campaign can be sent.',
      'Comparison Reports include a summary view.',
      'Order Reports include an "Others" section alongside the top 10 orders by dollar amount.',
      'Automatic calendar cleanup, recorded in the logs.',
    ],
    improvements: [
      'BCC copies are excluded from campaign analytics, so open and click rates reflect real recipients.',
      'Improved bot detection for email opens and clicks. A rule that could mark real recipients as bots was removed.',
      'Deactivated users no longer receive scheduled report emails.',
      'Clearer wording on the Data Sync page.',
    ],
    fixes: [
      'The Orders tab on the customer detail page shows orders again.',
      'Deadline notifications are sent correctly.',
      'User sync can no longer create duplicate users.',
      'Fixed a date conversion error that affected some records.',
      'Fixed calendar logging permissions.',
    ],
  },
  {
    month: '2026-07',
    title: 'July 2026',
    summary: 'Sales rep territories and more export options.',
    features: [
      'Territories field for every sales rep, shown on the Sales Rep page and recorded in the logs.',
      'Export on the Orders page.',
      '"Total Items" card on the Order Reports page.',
    ],
    improvements: [
      'The Sales Rep page and gamification include only users titled Sales Rep or Sales Manager.',
      'Changes to roles and permissions are logged.',
      'Data syncs run faster and more efficiently.',
    ],
    fixes: [
      'Corrected Customer Support role permissions.',
      'Calendar access works correctly when using "View As".',
      'The Territories column shows for non-admins.',
      'Several reporting fixes.',
    ],
  },
  {
    month: '2026-05',
    title: 'May 2026',
    features: [],
    improvements: ['Updated the Comparison Reports page.'],
    fixes: [],
  },
  {
    month: '2026-04',
    title: 'April 2026',
    summary: 'Calendar, the Data Sync page, better email campaigns, and filters that remember your place.',
    features: [
      'Calendar with role-based access and calendar sharing.',
      'Data Sync page, where admins run and monitor customer, order, contact and user syncs inside the CRM.',
      'Export on every report page.',
      'Email campaign builder with step-by-step wizard navigation and a redesigned segment builder.',
      'Task assignment emails, sent as a single digest when many tasks are created at once.',
      'Expanded Log Management for admins.',
      'Segments can be archived.',
    ],
    improvements: [
      'Every page remembers your filters and page position when you press Back.',
      'Customer and contact detail pages return you to where you came from.',
      'Users are deactivated instead of deleted, which keeps their audit history and blocks sign-in.',
      'Opportunity Smart Import captures phone numbers and fills in customer details.',
      'When an opportunity changes stage, its due date moves to 7 days out.',
      'Deleting an opportunity also removes its tasks and notifications.',
      'Reports match ApparelMagic totals, including quotes, and handle time zones correctly.',
      'Improved bot detection in campaign analytics.',
      'Report subscription digests for opportunities and tasks are sent correctly.',
    ],
    fixes: [
      'Campaigns report failures and send only from approved sender domains.',
      'BCC recipients receive each campaign email only once.',
      'Deferred emails no longer appear stuck as "pending retry".',
      'Each report type checks the correct permission, including email subscriptions.',
      'Fixed custom date ranges and filters on reports.',
      'Wide tables scroll properly on narrow screens.',
      'Fixed note permissions and saving.',
      'Opportunities flagged for re-engagement show who flagged them.',
    ],
  },
  {
    month: '2026-03',
    title: 'March 2026',
    summary: 'Re-engagement, bulk editing, a full permissions overhaul and "View As".',
    features: [
      'Re-engagement for Closed Lost and Not Qualified opportunities, with follow-up actions you can mark complete.',
      'Bulk Edit for tasks and opportunities.',
      'Workflows can create several tasks for one stage.',
      '"View As" lets admins see the CRM exactly as another user does.',
      'Knowledge Base lifecycle management, with separate admin pages for the AI Chatbot and knowledge.',
      'A "Disabled" role for users who should not have access.',
    ],
    improvements: [
      'Reworked role and custom permissions across the whole app.',
      'Lists remember which page you were on.',
      'Faster task completion.',
      'Customer Inactivity Report subscriptions run monthly.',
    ],
    fixes: [
      'The read-only admin role works correctly everywhere.',
      'Fixed the Full Name display on the Users page.',
      'Fixed report caching, charts, date filters and time zones.',
      'Fixed segment recipient counts and email campaign sending.',
      'Order reports include quotes, matching ApparelMagic.',
      'Fixed Smart Import validation.',
    ],
  },
  {
    month: '2026-02',
    title: 'February 2026',
    features: ['"Admin - View Only" role: see all data without being able to make changes.'],
    improvements: [
      'The achievement admin page is easier to use.',
      'Exports are clearer.',
      'Scheduled reports show the correct data for admins.',
    ],
    fixes: [],
  },
  {
    month: '2026-01',
    title: 'January 2026',
    summary: 'AI Chatbot, Knowledge Management, gamification and smarter tasks.',
    features: [
      'AI Chatbot powered by your Knowledge Base.',
      'Knowledge Management page for admins.',
      'Gamification: points, levels, achievements and leaderboards, with anti-abuse protections.',
      'Opportunity values: estimated ranges, with the actual value recorded when an opportunity is Closed Won.',
      'Task Intelligence: automatic tasks follow their opportunities, and are reopened or cleaned up when a stage change is undone.',
    ],
    improvements: [
      'The "Credit Approved" stage is now "Not Qualified" and is excluded from win rate and forecasts.',
      'Potential Value is required only after an opportunity leaves the New stage.',
      'Workflow tasks use the settings configured in the Workflow Editor.',
      'The Report Subscriptions card links to your subscription settings.',
    ],
    fixes: [
      'Report pages load correctly and respect permissions.',
      'Fixed sync notification emails and task associations with opportunities.',
    ],
  },
  {
    month: '2025-12',
    title: 'December 2025',
    summary: 'Fast search, workflow automation and the Customer Inactivity Report.',
    features: [
      'Fast search across customers, contacts, opportunities and orders.',
      'Tasks and notes can be linked to customers, contacts and opportunities.',
      'Smart Tasks with action items, auto-completion and a prompt to add a note.',
      'Workflow Automation: opportunity stage changes create follow-up tasks automatically.',
      'Customer Inactivity Report.',
      'Log Management for admins.',
      'Task priorities.',
      'Data export.',
      'Pipeline chart and Revenue (365 days) card on the Dashboard.',
    ],
    improvements: [
      '"Companies" are now "Customers" throughout the CRM, and contacts sync from ApparelMagic.',
      'Redesigned Dashboard and Tasks pages.',
      'The pipeline board handles large numbers of opportunities better.',
      'Opportunity Smart Import matches customers more accurately.',
    ],
    fixes: [
      'Fixed the win rate calculation.',
      'Corrected Item Revenue in Order Reports.',
      'Fixed the Notes tab count and "Load More" on the Orders page.',
      'Fixed role permission counts and custom permissions.',
      'Fixed report subscriptions.',
    ],
  },
  {
    month: '2025-11',
    title: 'November 2025',
    summary: 'Rebuilt reports, roles and permissions, notifications and customer sync.',
    features: [
      'Role and permission management.',
      'Customer sync from ApparelMagic.',
      'Rebuilt Reports section: Opportunity, Order, Task, Sales Rep and Comparison reports with zoomable charts.',
      'Notifications center with deadline alerts and personal settings.',
      'New opportunities automatically get follow-up tasks.',
      'Product Analytics and a customer filter on Order Reports.',
      'Email marketing: template ownership, campaign filtering and private campaigns.',
      'System Settings page for admins.',
    ],
    improvements: [
      'Reports load faster and cache their results.',
      'Clearer error messages, and offline support.',
      'Report revenue is credited to the primary sales rep.',
      'The default report range is 30 days.',
      'Managers are notified when a rep extends an opportunity due date.',
      'Readable record IDs.',
      'Refreshed sidebar icons and ordering.',
    ],
    fixes: [
      'Fixed date filtering, time zones and custom date ranges in reports.',
      'Order statuses are now based on fulfillment status.',
      'Segment and campaign recipient counts include only valid email addresses.',
      'Fixed sorting on several pages.',
      'Customer ID now maps to the account number.',
    ],
  },
  {
    month: '2025-10',
    title: 'October 2025',
    summary: 'Email Marketing, order details and a mobile-friendly app.',
    features: [
      'Email Marketing: campaigns, segments, a visual template editor, scheduling, and open/click tracking with analytics.',
      'Send a test email from the template preview.',
      'Order detail page.',
      'Forgot Password link on the sign-in page.',
      'Mobile-friendly layout.',
      'Opportunity import with duplicate detection.',
      'Customer review mode.',
    ],
    improvements: [
      'Email analytics show your most engaged customers and contacts.',
      'The Orders page shows the signed-in rep\'s orders by default.',
      'Reports have more filters and better charts.',
      'Merging customers also brings over their contacts.',
    ],
    fixes: [
      'Fixed date filtering in reports.',
      'Fixed customer search by domain name.',
      'Fixed the contact detail display, and contacts can now be deleted.',
    ],
  },
  {
    month: '2025-09',
    title: 'September 2025',
    summary: 'ApparelMagic integration and the pipeline board.',
    features: [
      'ApparelMagic integration: customers, orders and sales reps sync into the CRM.',
      'Drag-and-drop pipeline board on the Dashboard.',
      'Locations tab and contact detail view on customer pages.',
      'Admins can register new users.',
    ],
    improvements: [
      'Each user sees the data that belongs to them.',
      'Dates follow each user\'s format preference.',
      'Notes tabs show a count.',
      'The Orders page is faster.',
    ],
    fixes: [
      'Fixed missing orders.',
      'Fixed the Users and profile pages.',
      'The Focus field displays correctly.',
    ],
  },
  {
    month: '2025-08',
    title: 'August 2025',
    summary: 'Contacts, notes, Gmail and activity logs.',
    features: [
      'Contact notes, shown on the customer detail page.',
      'Gmail integration.',
      'Security settings and user profile pages.',
      'Win probability on opportunities.',
      'Activity logs on every page.',
    ],
    improvements: [
      'Redesigned Contacts and Customers pages.',
      'Territory shown on the sales rep page.',
    ],
    fixes: [
      'Opportunity notes save correctly.',
      'Better validation of required fields.',
    ],
  },
  {
    month: '2025-07',
    title: 'July 2025',
    summary: 'First version of the CRM.',
    features: [
      'Customers, opportunities and a sales pipeline with stages, including New Lead.',
      'Search, stage filters and notifications.',
    ],
    improvements: [],
    fixes: [],
  },
];

export const RELEASE_NOTE_CATEGORIES = [
  { key: 'features', label: 'New Features', icon: '✨', className: 'bg-green-50 text-green-800 border-green-200' },
  { key: 'improvements', label: 'Improvements', icon: '⚡', className: 'bg-blue-50 text-blue-800 border-blue-200' },
  { key: 'fixes', label: 'Bug Fixes', icon: '🐛', className: 'bg-amber-50 text-amber-800 border-amber-200' },
];
